import React, { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed or dismissed
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    
    if (dismissed || isStandalone) {
      return;
    }

    // Show iOS-specific prompt after delay
    if (isIOSDevice) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    // Handle beforeinstallprompt for Android/Desktop
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (!showPrompt) return null;

  // iOS-specific prompt with visual instructions
  if (isIOS) {
    return (
      <div className="fixed bottom-4 left-4 right-4 bg-white rounded-xl shadow-2xl border border-slate-200 p-4 z-50 animate-in slide-in-from-bottom-4">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1.5 hover:bg-slate-100 rounded-full"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4 text-slate-500" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-primary/10 rounded-xl shrink-0">
            <Download className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 pr-4">
            <h3 className="font-semibold text-slate-900 text-base">Install TradeHub24</h3>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
              Install this app on your iPhone:
            </p>
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-xs font-bold">1</span>
                <span className="text-slate-700">Tap the <Share className="inline h-4 w-4 mx-1 text-primary" /> Share button below</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-xs font-bold">2</span>
                <span className="text-slate-700">Scroll down, tap <strong>"Add to Home Screen"</strong></span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-xs font-bold">3</span>
                <span className="text-slate-700">Tap <strong>"Add"</strong> to install</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop prompt with Install button
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-white rounded-lg shadow-xl border border-slate-200 p-4 z-50 animate-in slide-in-from-bottom-4">
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 hover:bg-slate-100 rounded"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4 text-slate-500" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Download className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">Install TradeHub24</h3>
          <p className="text-sm text-slate-600 mt-1">
            Install our app for a better experience with offline access.
          </p>
          {deferredPrompt && (
            <Button 
              onClick={handleInstall} 
              size="sm" 
              className="mt-3"
            >
              Install App
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
